import { useKeyHints } from "../touch/use-key-hints.ts";
import { memo, useEffect, useLayoutEffect, useRef, useSyncExternalStore, type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactElement } from "react";
import { HUD } from "odyssey-shared";
import { ArtIcon, Glyph, ITEM_SLOT_ART_BOX, SmallNumber, glyphClass, logical } from "../../ui/kit/index.ts";
import { fitsSmallDigits } from "../../ui/kit/small-digits.ts";
import { ABILITY_SLOT, STATUS_ICON, slotFilled } from "../bar/layout.ts";
import type { ArrangingSnapshot, BarArranging } from "../bar/arranging.ts";
import { useBarDrag } from "../bar/drag.ts";
import type { BarSlotView, BarSource, EffectView } from "../bar/store.ts";
import { DragGhost } from "../drag/ghost.tsx";
import { dropValue } from "../drag/targets.ts";
import type { BarTicker } from "../bar/ticker.ts";
import type { TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import { slotLabel } from "../bar/words.ts";
import { abilityAction } from "../controls/actions.ts";
import { useKeyLabel } from "../controls/use-key.ts";
import { iconArt } from "../icons/art.ts";
import { itemArt, useIcons } from "../icons/context.tsx";
import { SWAP_TARGET } from "../bar/page-follow.ts";
import { pageOf } from "../bar/pages.ts";
import { barPaged, slotRect } from "./area-slots.ts";
import { BarSwap } from "./bar-swap.tsx";
import { HUD_SLOTS, resolveSlot, type HudArea } from "./slots.ts";
import { say } from "../../words.ts";

/**
 * The combat HUD of P2.2-T4 (the design's `Abilities` and `Effects` in `ui_kits/surfaces/CombatHud.jsx`, cards
 * `ui_kits/surfaces/cards/abilities.html` and `status.html`): the ability bar and the cast bar in their slots as the
 * area stands them (`slotRect`), the player's effects in `HUD_SLOTS.status` and the target's in
 * `HUD_SLOTS.targetStatus`. Each reads the bar's store (`bar/store.ts`) and draws only when it changes; what moves by
 * the frame (the wipe, the seconds, the times left) the ticker (`bar/ticker.ts`) writes into the elements each binds.
 * The slots are the only part that takes the pointer: a click on one uses it, as its key does, and never reaches the
 * world; the cast bar takes no pointer events. Each slot and each effect icon is a tooltip's anchor (P2.2-T12,
 * `bar/tooltip-view.tsx`): it names its place in `data-tip`, the bar and the rows hand the pointer, focus and clicks to
 * the tooltip's controller, an icon takes the pointer and keyboard focus for it, and a long press on a slot shows its
 * tooltip and never uses it.
 */

/**
 * Keeps a pointer press on a slot from taking focus, so a slot left focused never answers Space or Enter, which the game
 * captures, as the menu button does.
 *
 * @param event The press.
 */
function keepFocus(event: MouseEvent<HTMLElement>): void {
  event.preventDefault();
}

/**
 * Tells the page each time the calling component commits.
 *
 * @param onCommit What to tell, if anything.
 */
function useCommits(onCommit: (() => void) | undefined): void {
  useEffect(() => {
    onCommit?.();
  });
}

/**
 * A slot's classes: its state, whether it draws art, and, while the bar is arranged (P2.8-T29, ADR-121 decision 3), the
 * slot a drag carries (the design's `selected`), a drop's mark on the slot under it (the kit's drop rings), and
 * placing's outline in the Focus colour on every slot, as the touch design draws placing, with the focused slot's ring.
 *
 * @param slot The slot.
 * @param art Whether it draws art.
 * @param arranging What the bar draws while it is arranged.
 * @returns The class list.
 */
function slotClasses(slot: BarSlotView, art: boolean, arranging: ArrangingSnapshot): string {
  const classes = ["ot-hud-ability", `is-${slot.usable}`];
  if (art) {
    classes.push("has-art");
  }
  if (arranging.dragging === slot.index) {
    classes.push("is-selected");
  }
  if (arranging.over?.index === slot.index) {
    classes.push(arranging.over.tone === "positive" ? "is-drop-target" : "is-drop-refused");
  }
  if (arranging.placing !== null) {
    classes.push("is-placing");
    if (arranging.placing.from === slot.index) {
      classes.push("is-selected");
    }
  }
  return classes.join(" ");
}

/**
 * The bar's arranging when nothing arranges it.
 */
const NOT_ARRANGING: ArrangingSnapshot = Object.freeze({ dragging: null, over: null, placing: null });

/**
 * Hears nothing, for a bar nothing arranges.
 *
 * @returns Stops nothing.
 */
function hearNothing(): () => void {
  return () => undefined;
}

/**
 * Nothing arranging, for a bar nothing arranges.
 *
 * @returns The idle snapshot.
 */
function notArranging(): ArrangingSnapshot {
  return NOT_ARRANGING;
}

/**
 * One ability slot (the design's `AbilitySlot`): a 22 square button with its ability's 16 glyph, its key at the top left
 * while the viewer's Show key numbers on the bar is on (P2.18-T15; its name and tooltip say the key either way) and its
 * first cost at the bottom right in its resource's colour, each in the kit's small digits inside the edge (a key named
 * by a word, which the small digits cannot draw, in the pixel face); the tint of a slot short of its cost or locked, with
 * the lock glyph over a locked one; and the cooldown's wipe from the top of its well with the seconds on it, which the
 * ticker writes, hiding the key and the cost while it runs. The item slot (ADR-091 decision 14) prints the number
 * carried where a slot prints its cost, in the text colour, and keeps it through a cooldown. A slot draws its
 * ability's tile, or the item slot its item's art, when the page has it (ADR-111 decisions 15, 17 and 19): scaled for
 * the whole 22 slot, clipped inside its 1 pixel edge, with the wipe and the tints at inset 1 over it, so they cover
 * everything inside the edge, and the art greyed while locked. An empty slot draws the design's empty slot, asks for
 * no art and its click does nothing; it still takes the pointer and focus, so its tooltip says it is empty. A slot on
 * the page of a paged bar not shown is hidden and keeps its binding, so its wipe is right the moment it shows.
 *
 * @param props The `slot`, the `ticker` it binds its wipe to, whether it draws its key (`showKey`), whether it is on
 *   the page shown (`shown`), and `onUse`, which a click calls.
 * @returns The slot.
 */
function AbilitySlotView({ slot, ticker, showKey, shown, onUse, arranging, onPointerDown, onKeyDown, onFocus }: { readonly slot: BarSlotView; readonly ticker: BarTicker | null; readonly showKey: boolean; readonly shown: boolean; readonly onUse: (event: MouseEvent<HTMLButtonElement>) => void; readonly arranging: ArrangingSnapshot; readonly onPointerDown?: ((event: PointerEvent<HTMLElement>) => void) | undefined; readonly onKeyDown?: ((event: KeyboardEvent<HTMLButtonElement>) => void) | undefined; readonly onFocus?: (() => void) | undefined }): ReactElement {
  const button = useRef<HTMLButtonElement>(null);
  const wipe = useRef<HTMLSpanElement>(null);
  const seconds = useRef<HTMLSpanElement>(null);
  const icons = useIcons();
  const art = !slotFilled(slot) ? null : iconArt(icons.snapshot, slot.icon, slot.item === null ? `ability ${slot.name ?? slot.ability}` : `item ${slot.item}`, ABILITY_SLOT.size, icons.log);
  const well = art === null ? ABILITY_SLOT.well : ABILITY_SLOT.artWell;
  useEffect(() => {
    if (ticker === null || button.current === null || wipe.current === null || seconds.current === null) {
      return undefined;
    }
    ticker.bindSlot(slot.index, { slot: button.current, wipe: wipe.current, seconds: seconds.current, well });
    return () => ticker.bindSlot(slot.index, null);
  }, [ticker, slot.index, well]);
  const iconLeft = (ABILITY_SLOT.size - ABILITY_SLOT.icon) / 2;
  const key = useKeyLabel(abilityAction(slot.index));
  const keyed = useKeyHints();
  return (
    <button ref={button} type="button" className={slotClasses(slot, art !== null, arranging)} data-hud={`ability-${slot.key}`} data-tip={`slot:${slot.index}`} data-drop={dropValue({ surface: "bar", index: slot.index })} data-usable={slot.usable} hidden={!shown} aria-label={slotLabel(slot.name, key, slot.count, keyed)} aria-description={!slotFilled(slot) || !keyed ? undefined : say("bar.move")} aria-disabled={!slotFilled(slot)} onMouseDown={keepFocus} onPointerDown={onPointerDown} onKeyDown={onKeyDown} onFocus={onFocus} onClick={onUse} style={{ width: logical(ABILITY_SLOT.size), height: logical(ABILITY_SLOT.size) }}>
      {art !== null ? <ArtIcon art={art} room={ABILITY_SLOT.artWell} at={{ left: 0, top: 0 }} /> : slot.glyph === null ? null : <span className={`ot-hud-ability__icon ot-icon ${glyphClass(slot.glyph)}`} aria-hidden="true" style={{ left: logical(iconLeft - 1), top: logical(iconLeft - 1) }} />}
      <span className="ot-hud-ability__tint" aria-hidden="true" />
      <span ref={wipe} className="ot-hud-ability__wipe" data-hud="ability-wipe" aria-hidden="true" style={{ height: logical(0) }} />
      <span ref={seconds} className="ot-hud-ability__seconds" data-hud="ability-seconds" aria-hidden="true" />
      {!showKey ? null : fitsSmallDigits(key) ? (
        <SmallNumber text={key} className="ot-hud-ability__key" place={{ left: 0, top: 0 }} />
      ) : (
        <span className="ot-hud-ability__key" aria-hidden="true">
          {key}
        </span>
      )}
      {slot.cost === null ? null : <SmallNumber text={String(slot.cost.amount)} className={`ot-hud-ability__cost is-${slot.cost.resource}`} hud="ability-cost" place={{ right: 0, bottom: 0 }} />}
      {slot.count === null ? null : <SmallNumber text={String(slot.count)} className="ot-hud-ability__count" hud="ability-count" place={{ right: 0, bottom: 0 }} />}
      {slot.usable === "locked" ? (
        <span className="ot-hud-ability__lock" aria-hidden="true">
          <Glyph name="lock" />
        </span>
      ) : null}
    </button>
  );
}

/**
 * A key on a bar slot: Shift+Enter picks the slot up for placing and never uses it (ADR-121 decision 3.3); a focused
 * button is activated by Enter whatever the modifier, so the press's default is prevented and it goes no further.
 *
 * @param arrange The page's arranging.
 * @param event The key.
 * @param index The slot, from 0.
 */
function pressed(arrange: BarArranging, event: KeyboardEvent<HTMLButtonElement>, index: number): void {
  if (arrange.getSnapshot().placing === null && event.key === "Enter" && event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey && !event.repeat) {
    event.preventDefault();
    event.stopPropagation();
    arrange.startPlacingSlot(index);
  }
}

/**
 * The ability bar in its slot as the area stands it (`slotRect`): the ten slots a pixel apart, or, where the bar pages
 * (`barPaged`, P1.9-T133), the five of the page shown and the swap control after them. The bar tells its pages
 * whether the area pages it, so the swap's key and the key flash act only there; the page itself is kept across the
 * size. A swap drops a slot's tooltip, shown or pinned, whose slot the page no longer shows. While placing, focus is
 * put on the slot placing names once the page showing it is drawn, so it follows a page turned under it (P2.8-T30).
 * It starts the ticker while it is mounted.
 *
 * @param props The `area` it is laid out on, the bar's `source`, the `ticker` (null where nothing ticks, as under
 *   static rendering), the tooltip's controller (`tips`, null where no tooltip shows), whether the slots draw their
 *   keys (`keys`, the viewer's Show key numbers on the bar), and `onCommit`.
 * @returns The bar.
 */
export const AbilityBar = memo(function AbilityBar({ area, source, ticker, tips, keys, onCommit, arrange = null }: { readonly area: HudArea; readonly source: BarSource; readonly ticker: BarTicker | null; readonly tips: TipController | null; readonly keys: boolean; readonly onCommit: (() => void) | undefined; readonly arrange?: BarArranging | null }): ReactElement {
  useCommits(onCommit);
  const handlers = useTipHandlers(tips);
  const { slots } = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  const arranging = useSyncExternalStore(arrange?.subscribe ?? hearNothing, arrange?.getSnapshot ?? notArranging, arrange?.getSnapshot ?? notArranging);
  const drag = useBarDrag(arrange, tips);
  const group = useRef<HTMLDivElement>(null);
  const icons = useIcons();
  const dragged = arranging.dragging === null ? undefined : slots[arranging.dragging];
  useLayoutEffect(() => {
    if (arranging.dragging !== null) {
      drag.placeNow();
    }
  }, [arranging.dragging, drag]);
  const { page, flash } = useSyncExternalStore(source.pages.subscribe, source.pages.getSnapshot, source.pages.getSnapshot);
  const focused = arranging.placing?.focus ?? null;
  useEffect(() => {
    if (focused !== null) {
      group.current?.querySelector<HTMLElement>(`[data-drop='${dropValue({ surface: "bar", index: focused })}']`)?.focus();
    }
  }, [focused, page]);
  const paged = barPaged(area);
  useEffect(() => {
    ticker?.start();
    return () => ticker?.stop();
  }, [ticker]);
  useEffect(() => {
    source.pages.setPaged(paged);
  }, [source, paged]);
  useEffect(() => {
    const anchor = tips?.getSnapshot()?.anchor;
    if (paged && anchor?.surface === "slot" && pageOf(anchor.index) !== page) {
      tips?.clear();
    }
  }, [tips, paged, page]);
  const rect = slotRect("abilities", area);
  return (
    <div ref={group} className={`ot-hud-slot ot-hud-abilities${arranging.placing === null ? "" : " is-placing"}`} data-hud-slot="abilities" data-placing={arranging.placing === null ? undefined : "on"} role="group" aria-label={say("bar.bar")} {...handlers} style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height), columnGap: logical(ABILITY_SLOT.gap) }}>
      {slots.map((slot) => (
        <AbilitySlotView
          key={slot.index}
          slot={slot}
          ticker={ticker}
          showKey={keys}
          shown={!paged || pageOf(slot.index) === page}
          arranging={arranging}
          onPointerDown={arrange === null ? undefined : drag.onPointerDown}
          onKeyDown={arrange === null ? undefined : (event) => pressed(arrange, event, slot.index)}
          onFocus={arrange === null || arranging.placing === null ? undefined : () => arrange.focusAt(slot.index)}
          onUse={(event) => {
            if (drag.swallow()) {
              return;
            }
            if (arrange !== null && arranging.placing !== null) {
              arrange.placeAt(slot.index, event.shiftKey);
              return;
            }
            if (arrange !== null && arrange.heldItem() !== null) {
              arrange.placeHeld(slot.index, event.shiftKey);
              return;
            }
            if (tips?.click({ surface: "slot", index: slot.index }) !== true && slot.ability !== 0) {
              source.use(slot.index);
            }
          }}
        />
      ))}
      {dragged === undefined ? null : <DragGhost host={drag.ghost} label={dragged.name ?? say("bar.empty")} rarity={dragged.item === null ? null : (source.rules.items.get(dragged.item)?.rarity ?? null)} art={dragged.item === null ? iconArt(icons.snapshot, dragged.icon, `ability ${dragged.name ?? dragged.ability}`, ITEM_SLOT_ART_BOX, icons.log) : itemArt(icons, source.rules, dragged.item, ITEM_SLOT_ART_BOX)} glyph={dragged.glyph} tone={arranging.over?.tone ?? null} />}
      {paged ? <BarSwap page={page} flash={flash} showKey={keys} resting={arranging.over?.index === SWAP_TARGET} onSwap={() => source.pages.swap()} /> : null}
    </div>
  );
});

/**
 * The cast bar in its slot (the design's `CastBar`): no ability has a cast time yet, so it draws only the refused
 * state, the bar full in the danger's dark fill with the server's reason on it, for a refusal that cancelled a windup,
 * until the store ends it.
 *
 * @param props The `area` it is laid out on, the bar's `source`, and `onCommit`.
 * @returns The cast bar, or nothing.
 */
export const CastBar = memo(function CastBar({ area, source, onCommit }: { readonly area: HudArea; readonly source: BarSource; readonly onCommit: (() => void) | undefined }): ReactElement | null {
  useCommits(onCommit);
  const { cast } = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  if (cast === null) {
    return null;
  }
  const rect = slotRect("cast", area);
  return (
    <div className="ot-hud-slot" data-hud-slot="cast" style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      <div key={cast.serial} className="ot-hud-cast is-rejected" data-hud="cast" data-ability={cast.name} role="status" aria-label={`${say("bar.cast")}: ${cast.name}, ${cast.reason}`}>
        <span className="ot-hud-cast__words" data-hud="cast-reason">
          {cast.reason}
        </span>
      </div>
    </div>
  );
});

/**
 * One status icon (the design's `StatusIcon`): an 18 box ringed in brass for a buff and danger for a debuff, a debuff
 * with the corner fold so colour is never the only sign, its effect's 12 glyph, or its colour tile when the page has
 * it (ADR-111 decisions 15, 16 and 17: scaled for box 16 and clipped inside the tone ring), its stacks past one in the
 * kit's small digits at the bottom right inside the edge, and under it the time left, which the ticker writes and turns to the warning colour, blinking, near
 * its end. The fold and the stacks stand over the tile, and the blink is the box's, so the tile blinks with it.
 *
 * @param props The `effect`, the `row` and `index` it binds to the ticker under, and the `ticker`.
 * @returns The icon.
 */
function StatusIconView({ effect, row, index, ticker }: { readonly effect: EffectView; readonly row: "own" | "target"; readonly index: number; readonly ticker: BarTicker | null }): ReactElement {
  const icon = useRef<HTMLSpanElement>(null);
  const time = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (ticker === null || icon.current === null || time.current === null) {
      return undefined;
    }
    ticker.bindStatus(row, index, { icon: icon.current, time: time.current });
    return () => ticker.bindStatus(row, index, null);
  }, [ticker, row, index]);
  const icons = useIcons();
  const art = iconArt(icons.snapshot, effect.icon, `effect ${effect.name}`, STATUS_ICON.artBox, icons.log);
  const glyphLeft = (STATUS_ICON.box - STATUS_ICON.icon) / 2 - 1;
  const ring = (STATUS_ICON.box - 2 - STATUS_ICON.artRoom) / 2;
  return (
    <span className="ot-hud-effect" data-hud="effect" data-effect={effect.effect} data-tip={`${row}:${index}`} role="listitem" tabIndex={0} aria-label={effect.stacks > 1 ? `${effect.name}, ${effect.stacks}` : effect.name} style={{ width: logical(STATUS_ICON.column) }}>
      <span ref={icon} className={`ot-hud-effect__box is-${effect.tone}`} style={{ width: logical(STATUS_ICON.box), height: logical(STATUS_ICON.box) }}>
        {art !== null ? <ArtIcon art={art} room={STATUS_ICON.artRoom} at={{ left: ring, top: ring }} /> : effect.glyph === null ? null : <span className={`ot-hud-effect__icon ot-icon ${glyphClass(effect.glyph)}`} aria-hidden="true" style={{ left: logical(glyphLeft), top: logical(glyphLeft) }} />}
        {effect.tone === "debuff" ? <span className="ot-hud-effect__fold" aria-hidden="true" /> : null}
        {effect.stacks > 1 ? <SmallNumber text={String(effect.stacks)} className="ot-hud-effect__stacks" hud="effect-stacks" place={{ right: 0, bottom: 0 }} /> : null}
      </span>
      <span ref={time} className="ot-hud-effect__time" data-hud="effect-time" aria-hidden="true" style={{ marginTop: logical(STATUS_ICON.timeGap) }} />
    </span>
  );
}

/**
 * A row of effects in its slot: the player's under its card, or its target's under the target frame, as many columns
 * as the slot's width holds, in the order the server sent them.
 *
 * @param props The `area` it is laid out on, the `row`, its `effects`, the `ticker`, the left edge a target's row
 *   starts at (`left`, null for the slot's own), how far it is `drop`ped below its slot (0 for none), the tooltip's controller (`tips`), and `onCommit`.
 * @returns The row, or nothing for no effects.
 */
const StatusRow = memo(function StatusRow({ area, row, effects, ticker, left, drop = 0, tips, onCommit }: { readonly area: HudArea; readonly row: "own" | "target"; readonly effects: readonly EffectView[]; readonly ticker: BarTicker | null; readonly left: number | null; readonly drop?: number; readonly tips: TipController | null; readonly onCommit: (() => void) | undefined }): ReactElement | null {
  useCommits(onCommit);
  const handlers = useTipHandlers(tips);
  if (effects.length === 0) {
    return null;
  }
  const rect = resolveSlot(row === "own" ? HUD_SLOTS.status : HUD_SLOTS.targetStatus, area);
  const columns = Math.floor((rect.width + STATUS_ICON.gap) / (STATUS_ICON.column + STATUS_ICON.gap));
  return (
    <div className="ot-hud-slot ot-hud-status" data-hud-slot={row === "own" ? "status" : "targetStatus"} role="list" aria-label={row === "own" ? say("bar.own") : say("bar.target")} {...handlers} style={{ left: logical(left ?? rect.left), top: logical(rect.top + drop), width: logical(rect.width), height: logical(rect.height), columnGap: logical(STATUS_ICON.gap) }}>
      {effects.slice(0, columns).map((effect, index) => (
        <StatusIconView key={`${index}-${effect.effect}`} effect={effect} row={row} index={index} ticker={ticker} />
      ))}
    </div>
  );
});

/**
 * The target's effects row's left edge: its slot's, or a gutter right of the player card where the card reaches past
 * it, as the target frame does.
 *
 * @param area The area.
 * @param after The player card's right edge.
 * @returns The left edge in logical pixels.
 */
export function targetRowLeft(area: HudArea, after: number): number {
  return Math.max(resolveSlot(HUD_SLOTS.targetStatus, area).left, after + HUD.gutter);
}

/**
 * The player's and the target's effects rows.
 *
 * @param props The `area` it is laid out on, the bar's `source`, the `ticker`, whether the HUD shows a target frame
 *   (`targeted`), whether the connection mark shows, which stands in the player's row's place while it does
 *   (`held`), how far the player's row is `drop`ped under its slot to stand beneath a mark that does not hold it
 *   back, the player card's right edge (`after`), which the target's row keeps a gutter from as the frame does,
 *   the tooltip's controller (`tips`), and `onCommit`.
 * @returns The rows.
 */
export const StatusRows = memo(function StatusRows({ area, source, ticker, targeted, held = false, drop = 0, after, tips, onCommit }: { readonly area: HudArea; readonly held?: boolean; readonly drop?: number; readonly source: BarSource; readonly ticker: BarTicker | null; readonly targeted: boolean; readonly after: number; readonly tips: TipController | null; readonly onCommit: (() => void) | undefined }): ReactElement {
  const { own, target } = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot);
  const targetLeft = targetRowLeft(area, after);
  return (
    <>
      {held ? null : <StatusRow area={area} row="own" effects={own} ticker={ticker} left={null} drop={drop} tips={tips} onCommit={onCommit} />}
      {targeted ? <StatusRow area={area} row="target" effects={target} ticker={ticker} left={targetLeft} tips={tips} onCommit={onCommit} /> : null}
    </>
  );
});
